import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  fonts: {},
  sizes: {},
  colors: {},
  spacing: {},
  borders: {},
  shadows: {},
  isListening: false,
  error: null,
  lastUpdate: null,
};

const brandSlice = createSlice({
  name: 'brand',
  initialState,
  reducers: {
    setFonts: (state, action) => {
      state.fonts = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    setSizes: (state, action) => {
      state.sizes = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    setColors: (state, action) => {
      state.colors = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    setSpacing: (state, action) => {
      state.spacing = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    setBorders: (state, action) => {
      state.borders = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    setShadows: (state, action) => {
      state.shadows = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    setBrandData: (state, action) => {
      const data = action.payload || {};
      state.fonts = data.fonts || {};
      state.sizes = data.sizes || {};
      state.colors = data.colors || {};
      state.spacing = data.spacing || {};
      state.borders = data.borders || {};
      state.shadows = data.shadows || {};
      state.lastUpdate = new Date().toISOString();
    },

    setBrandListening: (state, action) => {
      state.isListening = action.payload;
    },

    setBrandError: (state, action) => {
      state.error = action.payload;
    },

    resetBrand: () => initialState,
  },
});

export const {
  setFonts,
  setSizes,
  setColors,
  setSpacing,
  setBorders,
  setShadows,
  setBrandData,
  setBrandListening,
  setBrandError,
  resetBrand,
} = brandSlice.actions;

// Selectors
export const selectFonts = (state) => state.brand.fonts;
export const selectSizes = (state) => state.brand.sizes;
export const selectColors = (state) => state.brand.colors;
export const selectSpacing = (state) => state.brand.spacing;
export const selectBorders = (state) => state.brand.borders;
export const selectShadows = (state) => state.brand.shadows;
export const selectBrandIsListening = (state) => state.brand.isListening;
export const selectBrandError = (state) => state.brand.error;

/**
 * Get font value for a specific location
 * @param {string} component - Component name (e.g., 'menu', 'header', 'card')
 * @param {string} element - Element name (e.g., 'title', 'subtitle', 'body')
 * @returns {function} - Selector function returning font config
 */
export const selectFont = (component, element) => (state) => {
  return state.brand.fonts?.[component]?.[element] ?? null;
};

/**
 * Get size value for a specific location
 * @param {string} component - Component name
 * @param {string} element - Element name
 * @returns {function} - Selector function returning size value
 */
export const selectSize = (component, element) => (state) => {
  return state.brand.sizes?.[component]?.[element] ?? null;
};

/**
 * Get color value for a specific location
 * @param {string} component - Component name
 * @param {string} element - Element name
 * @returns {function} - Selector function returning color value
 */
export const selectColor = (component, element) => (state) => {
  return state.brand.colors?.[component]?.[element] ?? null;
};

/**
 * Get spacing value for a specific location
 * @param {string} component - Component name
 * @param {string} element - Element name
 * @returns {function} - Selector function returning spacing value
 */
export const selectSpacingValue = (component, element) => (state) => {
  return state.brand.spacing?.[component]?.[element] ?? null;
};

/**
 * Get border value for a specific location
 * @param {string} component - Component name
 * @param {string} element - Element name
 * @returns {function} - Selector function returning border config
 */
export const selectBorder = (component, element) => (state) => {
  return state.brand.borders?.[component]?.[element] ?? null;
};

/**
 * Get shadow value for a specific location
 * @param {string} component - Component name
 * @param {string} element - Element name
 * @returns {function} - Selector function returning shadow value
 */
export const selectShadow = (component, element) => (state) => {
  return state.brand.shadows?.[component]?.[element] ?? null;
};

export default brandSlice.reducer;
